import React, { useEffect, useRef, useState, useCallback } from "react";
import api, { formatApiError, API } from "@/lib/api";
import { money, fmtDate } from "@/lib/locale";
import { ensureUrl } from "@/lib/url";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { EmptyState } from "@/components/PageHeader";
import { Receipt, Plus, Trash2, Phone, Mail, Globe, FileUp, FileText, Download, Hash, Calendar, X } from "lucide-react";
import ScanStrip from "@/components/ScanStrip";
import { toast } from "sonner";

export default function Warranties() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [presetReasons, setPresetReasons] = useState([]);
  const blank = {
    item_name: "", brand: "", model_number: "", serial_number: "",
    purchase_date: "", expiry_date: "", purchase_price: "",
    supplier_name: "", supplier_phone: "", supplier_email: "", supplier_website: "",
    claim_reasons: [], important_dates: [], notes: "",
  };
  const [form, setForm] = useState(blank);
  const [scanConfidence, setScanConfidence] = useState(null);

  const applyScannedWarranty = (data) => {
    // Preserve any user-entered notes / claim reasons / important dates;
    // overwrite only the fields the scanner extracted.
    setForm((prev) => ({
      ...prev,
      item_name: data.item_name || prev.item_name,
      brand: data.brand || prev.brand,
      model_number: data.model_number || prev.model_number,
      serial_number: data.serial_number || prev.serial_number,
      purchase_date: data.purchase_date || prev.purchase_date,
      expiry_date: data.expiry_date || prev.expiry_date,
      purchase_price: data.purchase_price != null ? String(data.purchase_price) : prev.purchase_price,
      supplier_name: data.supplier_name || prev.supplier_name,
    }));
  };
  const [customReason, setCustomReason] = useState("");
  const [newDateLabel, setNewDateLabel] = useState("");
  const [newDateValue, setNewDateValue] = useState("");
  const fileRef = useRef(null);
  const [uploadingFor, setUploadingFor] = useState(null);

  const load = useCallback(async () => { const { data } = await api.get("/warranties"); setItems(data); }, []);
  useEffect(() => {
    load();
    api.get("/warranties/claim-reasons").then(({ data }) => setPresetReasons(data.presets || [])).catch(() => {});
  }, [load]);

  const save = async () => {
    if (!form.item_name.trim()) return toast.error("Item name is required");
    try {
      const payload = {
        ...form,
        supplier_website: ensureUrl(form.supplier_website),
        purchase_price: form.purchase_price ? parseFloat(form.purchase_price) : null,
      };
      if (editId) await api.put(`/warranties/${editId}`, payload);
      else await api.post("/warranties", payload);
      setOpen(false); setForm(blank); setEditId(null); toast.success("Saved"); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const startEdit = (it) => {
    setForm({
      ...blank, ...it,
      claim_reasons: it.claim_reasons || [],
      important_dates: it.important_dates || [],
    });
    setEditId(it.id); setOpen(true);
  };
  const remove = async (id) => { await api.delete(`/warranties/${id}`); load(); };

  const toggleReason = (r) => {
    setForm((f) => f.claim_reasons.includes(r)
      ? { ...f, claim_reasons: f.claim_reasons.filter((x) => x !== r) }
      : { ...f, claim_reasons: [...f.claim_reasons, r] });
  };
  const addCustomReason = () => {
    const r = customReason.trim();
    if (r && !form.claim_reasons.includes(r)) {
      setForm({ ...form, claim_reasons: [...form.claim_reasons, r] });
      setCustomReason("");
    }
  };
  const addImportantDate = () => {
    if (!newDateLabel.trim() || !newDateValue) return;
    setForm({ ...form, important_dates: [...(form.important_dates || []), { label: newDateLabel.trim(), date: newDateValue }] });
    setNewDateLabel(""); setNewDateValue("");
  };
  const removeImportantDate = (idx) => {
    setForm({ ...form, important_dates: form.important_dates.filter((_, i) => i !== idx) });
  };

  const uploadDoc = async (id, file) => {
    if (!file) return;
    setUploadingFor(id);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/warranties/${id}/document`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document attached"); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setUploadingFor(null); }
  };

  const isExpired = (d) => d && new Date(d) < new Date();

  return (
    <div className="archive">
      <div className="archive__page" data-testid="archive-page">
        <header className="mb-8">
          <p className="archive__eyebrow" data-testid="archive-eyebrow">The Archive</p>
          <h1 className="archive__title text-4xl md:text-5xl lg:text-6xl mt-2">
            Warranties, filed with care.
          </h1>
          <p className="archive__lede mt-3 max-w-2xl text-lg md:text-xl">
            Receipts, serial numbers, brand hotlines — a hushed drawer for the
            moment something breaks and you need to move quickly.
          </p>
          <div className="mt-6">
            <button
              data-testid="warr-add-button"
              onClick={() => { setEditId(null); setForm(blank); setScanConfidence(null); setOpen(true); }}
              className="btn-slate inline-flex items-center text-sm"
            >
              <Plus size={15} className="mr-1.5" /> File a new warranty
            </button>
          </div>
        </header>

        {items.length === 0 ? (
          <EmptyState icon={Receipt} title="The drawer is empty." message="File appliances, electronics and big purchases so you never lose a receipt again." />
        ) : (
          <div className="space-y-4">
            {items.map((it) => {
              const expired = isExpired(it.expiry_date);
              return (
                <div key={it.id} data-testid={`warr-row-${it.id}`} className="index-card p-6 fade-up relative">
                  <span className="brass-tab" data-testid={`warr-tab-${it.id}`}>{expired ? "Expired" : "On file"}</span>
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <span className={`w-2.5 h-2.5 rounded-full ${expired ? "bg-hearth-coral" : ""} shrink-0`}
                            style={!expired ? { background: "var(--brass)" } : undefined} />
                      <div className="min-w-0">
                        <h3 className="font-display text-2xl font-semibold truncate" style={{ color: "var(--ink)" }}>{it.item_name}</h3>
                        <p className="caption mt-0.5" style={{ color: "var(--slate-soft)" }}>
                          {it.brand || "—"} {it.expiry_date && <span> · {expired ? "Expired" : "Valid until"} {fmtDate(it.expiry_date)}</span>}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {it.supplier_phone && <a href={`tel:${it.supplier_phone}`} className="btn-slate-ghost text-xs inline-flex items-center gap-1"><Phone size={12} /> Call</a>}
                      {it.supplier_email && <a href={`mailto:${it.supplier_email}`} className="btn-slate-ghost text-xs inline-flex items-center gap-1"><Mail size={12} /> Email</a>}
                      {it.supplier_website && <a href={ensureUrl(it.supplier_website)} target="_blank" rel="noreferrer" data-testid={`warr-website-${it.id}`} className="btn-slate-ghost text-xs inline-flex items-center gap-1"><Globe size={12} /> Website</a>}
                      <Button variant="ghost" data-testid={`warr-edit-${it.id}`} onClick={() => startEdit(it)} className="rounded-full text-sm">Edit</Button>
                      <Button variant="ghost" data-testid={`warr-delete-${it.id}`} onClick={() => remove(it.id)} className="rounded-full text-sm text-hearth-coral hover:bg-hearth-coral/10"><Trash2 size={14} /></Button>
                    </div>
                  </div>
                  <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                    {it.purchase_date && <div><p className="caption" style={{ color: "var(--slate-soft)" }}>Purchased</p><p className="mt-1" style={{ color: "var(--ink)" }}>{fmtDate(it.purchase_date)}</p></div>}
                    {it.purchase_price != null && it.purchase_price > 0 && <div><p className="caption" style={{ color: "var(--slate-soft)" }}>Price</p><p className="mt-1 font-mono" style={{ color: "var(--ink)" }}>{money(it.purchase_price)}</p></div>}
                    {it.supplier_name && <div><p className="caption" style={{ color: "var(--slate-soft)" }}>Supplier</p><p className="mt-1" style={{ color: "var(--ink)" }}>{it.supplier_name}</p></div>}
                    {it.model_number && <div><p className="caption" style={{ color: "var(--slate-soft)" }}>Model No.</p><p className="mt-1 font-mono" style={{ color: "var(--ink)" }}>{it.model_number}</p></div>}
                    {it.serial_number && <div><p className="caption" style={{ color: "var(--slate-soft)" }}>Serial No.</p><p className="mt-1 font-mono" style={{ color: "var(--ink)" }}>{it.serial_number}</p></div>}
                  </div>
                  {(it.important_dates?.length || 0) > 0 && (
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {it.important_dates.map((d, i) => (
                        <span key={`${d.label}-${d.date}-${i}`} className="chip inline-flex items-center gap-1" style={{ background: "rgba(176,138,72,0.14)", color: "#7A5320", border: "1px solid rgba(176,138,72,0.35)" }}><Calendar size={11} /> {d.label}: {fmtDate(d.date)}</span>
                      ))}
                    </div>
                  )}
                  {(it.claim_reasons?.length || 0) > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      <span className="caption" style={{ color: "var(--slate-soft)" }}>Claim reasons:</span>
                      {it.claim_reasons.map((r, i) => (
                        <span key={`${r}-${i}`} className="chip bg-hearth-coral/12 text-hearth-coral">{r}</span>
                      ))}
                    </div>
                  )}
                  {it.notes && <p className="mt-3 text-sm" style={{ color: "var(--slate-soft)" }}>{it.notes}</p>}
                  <div className="mt-4 pt-4 flex items-center gap-3 flex-wrap" style={{ borderTop: "1px dashed rgba(58,66,77,0.15)" }}>
                    <label className="btn-slate-ghost text-xs inline-flex items-center gap-1.5 cursor-pointer" data-testid={`warr-upload-${it.id}`}>
                      <FileUp size={13} /> {uploadingFor === it.id ? "Uploading…" : "Attach document"}
                      <input type="file" hidden accept="application/pdf,image/*" onChange={(e) => { uploadDoc(it.id, e.target.files?.[0]); e.target.value = ""; }} />
                    </label>
                    {(it.documents || []).map((d) => (
                      <a key={d.id} href={`${API}/warranties/${it.id}/document/${d.id}`} target="_blank" rel="noreferrer"
                         className="pill-btn text-xs inline-flex items-center gap-1.5"
                         style={{ background: "rgba(176,138,72,0.12)", color: "#7A5320", border: "1px solid rgba(176,138,72,0.35)" }}>
                        <FileText size={13} /> {d.filename} <Download size={12} />
                      </a>
                    ))}
                    {(it.documents || []).length > 0 && (
                      <span className="filed-note ml-auto" data-testid={`warr-filed-note-${it.id}`}>filed</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-3xl border-hearth-line bg-hearth-paper max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-3xl">{editId ? "Edit warranty" : "Add a warranty"}</DialogTitle>
            <DialogDescription className="sr-only">Fill or scan the details for a purchase you want to track under warranty.</DialogDescription>
          </DialogHeader>

          {/* v27.19 — Scan a receipt or warranty card → prefill fields.
              Hidden in edit mode so we don't accidentally overwrite the
              user's existing warranty. */}
          {!editId && (
            <ScanStrip
              testId="warr-scan"
              label="Snap the receipt or warranty card — we'll fill it in."
              buttonLabel="Scan receipt"
              endpoint="/warranties/scan-receipt"
              confidence={scanConfidence}
              onConfidence={setScanConfidence}
              onScanned={applyScannedWarranty}
              successPrefix="Filled from your receipt"
            />
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label className="caption text-hearth-mute">Item</Label>
              <Input data-testid="warr-form-item" className="input-warm mt-1.5 h-11" value={form.item_name} onChange={(e) => setForm({ ...form, item_name: e.target.value })} placeholder="Bosch washing machine" />
            </div>
            <div>
              <Label className="caption text-hearth-mute">Brand</Label>
              <Input className="input-warm mt-1.5 h-11" value={form.brand || ""} onChange={(e) => setForm({ ...form, brand: e.target.value })} placeholder="e.g. Bosch" />
            </div>
            <div>
              <Label className="caption text-hearth-mute">Purchase price (AUD)</Label>
              <Input type="number" step="0.01" className="input-warm mt-1.5 h-11" value={form.purchase_price || ""} onChange={(e) => setForm({ ...form, purchase_price: e.target.value })} />
            </div>
            <div>
              <Label className="caption text-hearth-mute inline-flex items-center gap-1"><Hash size={11} /> Model number</Label>
              <Input data-testid="warr-form-model" className="input-warm mt-1.5 h-11 font-mono" value={form.model_number || ""} onChange={(e) => setForm({ ...form, model_number: e.target.value })} />
            </div>
            <div>
              <Label className="caption text-hearth-mute inline-flex items-center gap-1"><Hash size={11} /> Serial number</Label>
              <Input data-testid="warr-form-serial" className="input-warm mt-1.5 h-11 font-mono" value={form.serial_number || ""} onChange={(e) => setForm({ ...form, serial_number: e.target.value })} />
            </div>
            <div>
              <Label className="caption text-hearth-mute">Purchase date</Label>
              <Input type="date" className="input-warm mt-1.5 h-11" value={form.purchase_date || ""} onChange={(e) => setForm({ ...form, purchase_date: e.target.value })} />
            </div>
            <div>
              <Label className="caption text-hearth-mute">Warranty expiry</Label>
              <Input type="date" className="input-warm mt-1.5 h-11" value={form.expiry_date || ""} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} />
            </div>
            <div>
              <Label className="caption text-hearth-mute">Supplier name</Label>
              <Input className="input-warm mt-1.5 h-11" value={form.supplier_name || ""} onChange={(e) => setForm({ ...form, supplier_name: e.target.value })} />
            </div>
            <div>
              <Label className="caption text-hearth-mute">Supplier phone</Label>
              <Input className="input-warm mt-1.5 h-11" value={form.supplier_phone || ""} onChange={(e) => setForm({ ...form, supplier_phone: e.target.value })} placeholder="+61 1300 XXX XXX" />
            </div>
            <div>
              <Label className="caption text-hearth-mute">Supplier email</Label>
              <Input className="input-warm mt-1.5 h-11" value={form.supplier_email || ""} onChange={(e) => setForm({ ...form, supplier_email: e.target.value })} />
            </div>
            <div>
              <Label className="caption text-hearth-mute">Supplier website</Label>
              <Input data-testid="warr-form-website" className="input-warm mt-1.5 h-11" value={form.supplier_website || ""} onChange={(e) => setForm({ ...form, supplier_website: e.target.value })} placeholder="e.g. bosch-home.com.au" />
            </div>

            <div className="md:col-span-2">
              <Label className="caption text-hearth-mute">Important dates <span className="text-hearth-mute font-normal">(e.g. Extended warranty end, First service due)</span></Label>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {(form.important_dates || []).map((d, i) => (
                  <span key={`${d.label}-${d.date}-${i}`} className="chip bg-hearth-amber/15 text-[#7A5320] inline-flex items-center gap-1.5">
                    <Calendar size={11} /> {d.label}: {d.date}
                    <button onClick={() => removeImportantDate(i)} className="hover:text-hearth-coral"><X size={11} /></button>
                  </span>
                ))}
              </div>
              <div className="mt-2 flex gap-2">
                <Input className="input-warm h-9 flex-1" placeholder="Label (e.g. Extended warranty end)" value={newDateLabel} onChange={(e) => setNewDateLabel(e.target.value)} />
                <Input type="date" className="input-warm h-9" value={newDateValue} onChange={(e) => setNewDateValue(e.target.value)} />
                <Button variant="ghost" onClick={addImportantDate} className="rounded-full h-9 px-3 border border-hearth-line">Add</Button>
              </div>
            </div>

            <div className="md:col-span-2">
              <Label className="caption text-hearth-mute">Common claim reasons <span className="text-hearth-mute font-normal">(tap to toggle)</span></Label>
              <div className="mt-2 flex flex-wrap gap-1.5" data-testid="warr-form-reasons">
                {presetReasons.map((r) => {
                  const on = form.claim_reasons.includes(r);
                  return (
                    <button key={r} type="button" onClick={() => toggleReason(r)}
                      className={`chip ${on ? "bg-hearth-coral text-white" : "bg-hearth-stone text-hearth-mute hover:bg-hearth-line"}`}>
                      {r}
                    </button>
                  );
                })}
                {form.claim_reasons.filter((r) => !presetReasons.includes(r)).map((r) => (
                  <span key={r} className="chip bg-hearth-coral/15 text-hearth-coral inline-flex items-center gap-1">
                    {r}
                    <button onClick={() => toggleReason(r)} className="hover:text-hearth-coral"><X size={10} /></button>
                  </span>
                ))}
              </div>
              <div className="mt-2 flex gap-2">
                <Input className="input-warm h-9 flex-1" placeholder="Or add your own reason…" value={customReason} onChange={(e) => setCustomReason(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustomReason(); } }} />
                <Button variant="ghost" onClick={addCustomReason} className="rounded-full h-9 px-3 border border-hearth-line">Add</Button>
              </div>
            </div>

            <div className="md:col-span-2">
              <Label className="caption text-hearth-mute">Notes</Label>
              <Textarea className="input-warm mt-1.5" rows={2} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} className="rounded-full">Cancel</Button>
            <button data-testid="warr-form-save" onClick={save} className="btn-slate inline-flex items-center text-sm">Save to the archive</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );
}
